import {
  CreditCard, Palette, GraduationCap, LogOut, ChevronLeft, ChevronRight, ChevronDown,
  ArrowRight, Sparkles, RotateCcw, Mail, Lock, LockOpen, User, Sun, Moon,
  PlayCircle, Check, CheckCircle, Info, AlertTriangle, Send, Eye, Brush,
  Droplet, Layers, FlaskConical, Heart, Clock, Globe, Hexagon, Scan,
  Share2, Search, CalendarDays, History, Users, X, Copy, Trophy, Gamepad2,
  Instagram, Youtube, AtSign, Music, Link2, MapPin, Inbox, MessageCircle, ImagePlus, Maximize,
  SlidersHorizontal, ToggleRight, Wallet, Wrench, Plus, Trash2, Save, Zap,
  Printer, FileText, Ticket, Home, Flag,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// ── Icon ──────────────────────────────────────────────────────────────────────

const ICONS: Record<string, LucideIcon> = {
  palette: Palette,
  graduation: GraduationCap,
  logout: LogOut,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronDown: ChevronDown,
  arrowRight: ArrowRight,
  sparkles: Sparkles,
  zap: Zap,
  rotate: RotateCcw,
  mail: Mail,
  lock: Lock,
  lockOpen: LockOpen,
  user: User,
  sun: Sun,
  moon: Moon,
  play: PlayCircle,
  check: Check,
  checkCircle: CheckCircle,
  info: Info,
  alert: AlertTriangle,
  send: Send,
  eye: Eye,
  brush: Brush,
  droplet: Droplet,
  layers: Layers,
  flask: FlaskConical,
  heart: Heart,
  clock: Clock,
  globe: Globe,
  hexagon: Hexagon,
  scan: Scan,
  share: Share2,
  search: Search,
  calendar: CalendarDays,
  history: History,
  users: Users,
  x: X,
  copy: Copy,
  trophy: Trophy,
  gamepad: Gamepad2,
  instagram: Instagram,
  youtube: Youtube,
  atSign: AtSign,
  music: Music,
  link: Link2,
  mapPin: MapPin,
  inbox: Inbox,
  message: MessageCircle,
  image: ImagePlus,
  maximize: Maximize,
  sliders: SlidersHorizontal,
  toggle: ToggleRight,
  wallet: Wallet,
  card: CreditCard,
  wrench: Wrench,
  plus: Plus,
  trash: Trash2,
  save: Save,
  printer: Printer,
  fileText: FileText,
  ticket: Ticket,
  home: Home,
  flag: Flag,
};

export function Icon({
  name, size = 22, stroke = 1.9,
}: { name: string; size?: number; stroke?: number }) {
  const C = ICONS[name];
  if (!C) return null;
  return <C size={size} strokeWidth={stroke} aria-hidden />;
}

// ── BeeMark ───────────────────────────────────────────────────────────────────

export function BeeMark({ size = 40 }: { size?: number }) {
  const s = size;
  return (
    <span className="beemark" style={{ width: s, height: s }}>
      <svg
        width={s * 0.62} height={s * 0.62} viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 2.6c-1.3 0-2.4 1-2.4 2.3 0 .6.3 1.2.7 1.6" />
        <path d="M12 2.6c1.3 0 2.4 1 2.4 2.3 0 .6-.3 1.2-.7 1.6" />
        <ellipse cx="12" cy="14" rx="4.4" ry="6" />
        <path d="M7.7 11.2h8.6M7.6 14.6h8.8M9 18h6" />
        <path d="M7.6 9.2C4.8 7.4 2.6 8.2 3 10.4c.3 1.7 2.4 2.4 4.6 1.6" />
        <path d="M16.4 9.2c2.8-1.8 5-1 4.6 1.2-.3 1.7-2.4 2.4-4.6 1.6" />
      </svg>
    </span>
  );
}

// ── ChoiceRow ─────────────────────────────────────────────────────────────────

export function ChoiceRow<T extends string | boolean>({
  label, hint, value, options, onChange, violet,
}: {
  label?: string;
  hint?: string;
  value: T | undefined;
  options: { val: T; label: string; swatch?: string; image?: string; imageLarge?: string }[];
  onChange: (v: T) => void;
  violet?: boolean;
}) {
  // Full-screen photo preview: hover on desktop, long-press (~350ms) on touch.
  const [preview, setPreview] = useState<{ src: string; label: string } | null>(null);
  const touchTimer = useRef<number | null>(null);
  const isTouch = useRef(false);          // suppress emulated mouse events on touch devices
  const suppressClick = useRef(false);    // a long-press release must not select the option

  const clearTouch = () => {
    if (touchTimer.current != null) { window.clearTimeout(touchTimer.current); touchTimer.current = null; }
    setPreview(null);
  };
  useEffect(() => clearTouch, []);

  return (
    <div>
      {label && <p className="t-sm muted" style={{ marginBottom: hint ? 3 : 9 }}>{label}</p>}
      {hint && <p className="t-xs muted-2" style={{ marginBottom: 9 }}>{hint}</p>}
      <div className="wraprow" role="group" aria-label={label}>
        {options.map((o) => {
          const big = o.imageLarge ?? o.image;
          const show = () => big && setPreview({ src: big, label: o.label });
          return (
            <button
              key={String(o.val)}
              type="button"
              aria-pressed={value === o.val}
              onClick={() => {
                if (suppressClick.current) { suppressClick.current = false; return; }
                onChange(o.val);
              }}
              onMouseEnter={() => { if (o.image && !isTouch.current) show(); }}
              onMouseLeave={() => { if (!isTouch.current) setPreview(null); }}
              onTouchStart={() => {
                isTouch.current = true;
                if (!o.image) return;
                touchTimer.current = window.setTimeout(() => {
                  suppressClick.current = true;
                  show();
                }, 350);
              }}
              onTouchEnd={clearTouch}
              onTouchCancel={clearTouch}
              onTouchMove={clearTouch}
              onContextMenu={(e) => { if (o.image) e.preventDefault(); }}
              className={"pill" + (violet ? " pill-violet" : "") + (o.image ? " pill--img" : "")}
            >
              {o.image && <img src={o.image} alt="" className="pill__img" loading="lazy" draggable={false} />}
              {!o.image && o.swatch && <span className="pill__swatch" style={{ background: o.swatch }} />}
              {o.label}
            </button>
          );
        })}
      </div>
      {preview && (
        <div className="img-preview" aria-hidden>
          <img src={preview.src} alt="" />
          <span>{preview.label}</span>
        </div>
      )}
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────────────────────

export function Field({
  icon, type = "text", value, onChange, placeholder, autoComplete, name,
}: {
  icon?: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  name?: string;
}) {
  return (
    <label className="field" style={{ display: "flex" }}>
      {icon && <Icon name={icon} size={19} />}
      <input
        className={"input" + (icon ? "" : " input--bare")}
        type={type}
        name={name}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </label>
  );
}

// ── Banner ────────────────────────────────────────────────────────────────────

export function Banner({
  kind = "info", children,
}: { kind?: "info" | "error" | "success"; children: React.ReactNode }) {
  const iconName = kind === "error" ? "alert" : kind === "success" ? "checkCircle" : "info";
  return (
    <div className={`banner banner-${kind}`} role={kind === "error" ? "alert" : "status"}>
      <Icon name={iconName} size={18} />
      <span>{children}</span>
    </div>
  );
}

// ── SectionHead ───────────────────────────────────────────────────────────────

export function SectionHead({
  icon, children, accent,
}: { icon?: string; children: React.ReactNode; accent?: "violet" | "amber" }) {
  return (
    <div className="row" style={{ marginBottom: 12, gap: 9 }}>
      {icon && (
        <span style={{
          display: "grid", placeItems: "center", width: 30, height: 30,
          borderRadius: 9,
          color: accent === "violet" ? "var(--violet-text)" : "var(--accent-text)",
          background: accent === "violet" ? "var(--violet-tint)" : "var(--accent-tint)",
        }}>
          <Icon name={icon} size={17} stroke={2.1} />
        </span>
      )}
      <h2 className="t-h">{children}</h2>
    </div>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { cls: string; icon: string | null }> = {
    not_started: { cls: "", icon: null },
    in_progress: { cls: "badge-amber", icon: "clock" },
    submitted: { cls: "badge-info", icon: "scan" },
    reviewed: { cls: "badge-violet", icon: "eye" },
    done: { cls: "badge-success", icon: "check" },
  };
  const m = map[status] ?? map.not_started;
  return (
    <span className={`badge ${m.cls}`}>
      {m.icon ? <Icon name={m.icon} size={12} stroke={2.4} /> : <span className="badge-dot" />}
      {t(`study.status_${status}`)}
    </span>
  );
}

// ── LangSwitch ────────────────────────────────────────────────────────────────

export function LangSwitch() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "ru";
  return (
    <div className="seg" role="group" aria-label="Language">
      {(["en", "ru"] as const).map((l) => (
        <button
          key={l}
          aria-pressed={lang === l}
          onClick={() => i18n.changeLanguage(l)}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ── ThemeToggle ───────────────────────────────────────────────────────────────

export function ThemeToggle({
  theme, onThemeChange,
}: { theme: "dark" | "light"; onThemeChange: (t: "dark" | "light") => void }) {
  const { t } = useTranslation();
  const next = theme === "dark" ? "light" : "dark";
  return (
    <button
      className="icon-btn"
      onClick={() => onThemeChange(next)}
      aria-label={`${t("common.theme")}: ${next}`}
      title={t("common.theme")}
    >
      <Icon name={theme === "dark" ? "sun" : "moon"} size={20} />
    </button>
  );
}

// ── AppHeader ─────────────────────────────────────────────────────────────────

export function AppHeader({
  title, onBack, onLogout, theme, onThemeChange, showLang = true,
}: {
  title?: string;
  onBack?: () => void;
  onLogout?: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
  showLang?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <header
      className="between"
      style={{
        padding: "14px 18px", position: "sticky", top: 0, zIndex: 10,
        backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        background: "linear-gradient(to bottom, var(--ambient), transparent)",
      }}
    >
      <div className="row" style={{ gap: 10, minWidth: 0, flex: "1 1 auto" }}>
        {onBack ? (
          <button className="icon-btn" onClick={onBack} aria-label={t("selection.back")}>
            <Icon name="chevronLeft" size={20} />
          </button>
        ) : (
          <BeeMark size={38} />
        )}
        <div style={{ minWidth: 0 }}>
          <div
            className="t-h"
            style={{ lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {title || t("app.title")}
          </div>
        </div>
      </div>
      <div className="row" style={{ gap: 8, flex: "none" }}>
        {!onBack && showLang && <LangSwitch />}
        <ThemeToggle theme={theme} onThemeChange={onThemeChange} />
        {onLogout && (
          <button className="icon-btn" onClick={onLogout} aria-label={t("menu.logout")}>
            <Icon name="logout" size={19} />
          </button>
        )}
      </div>
    </header>
  );
}
